import React, { useState, useRef } from 'react';
import { Popup } from '../ConflictBadge';
import PackIcon from '../PackIcon';

// Ícono chico de vínculo (cadena) — el equipo viene/va de otro evento.
function LinkGlyph({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, marginRight: 3, verticalAlign: '-1px', opacity: 0.85 }} aria-hidden="true">
      <path d="M9 17H7A5 5 0 0 1 7 7h2" />
      <path d="M15 7h2a5 5 0 0 1 0 10h-2" />
      <line x1="8" y1="12" x2="16" y2="12" />
    </svg>
  );
}

// Triángulo de advertencia — el pedido excede el stock disponible.
function WarnGlyph({ size = 11 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0 }} aria-hidden="true">
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

// Chip de stock consolidado. Si lo consolidado supera lo disponible real
// (total − lo que usan otros eventos), se pinta amarillo y al pasar el mouse
// muestra el popup con los eventos en conflicto + cuántas unidades faltan.
// El click NO abre el popup (burbujea al badge, que abre el buscador): el
// conflicto se ve por hover, con un pequeño delay para poder bajar el mouse
// hasta el popup e interactuar con él.
export default function StockChip({ label, group, conflict, onRemove, removable = true, onEventClick, linked = false }) {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const closeTimer = useRef(null);

  // Unidades pedidas de más = demanda total de eventos solapados − operativas.
  // Hay conflicto SOLO si te pasaste (déficit > 0): asignar 20 de 20 (déficit
  // 0) NO es conflicto; recién a 21 (déficit 1) sí.
  const deficit = conflict ? Math.max(0, (conflict.demand || 0) - conflict.total) : 0;
  const over = deficit > 0;
  // Un pack se identifica porque sus links tienen pack_id.
  const isPack = !!group?.some(l => l.pack_id);

  const openPopup = () => {
    clearTimeout(closeTimer.current);
    const r = ref.current?.getBoundingClientRect();
    if (r) { setRect(r); setOpen(true); }
  };
  const scheduleClose = () => {
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setOpen(false), 220);
  };
  const cancelClose = () => clearTimeout(closeTimer.current);

  return (
    <span
      ref={ref}
      className={`ic-chip${linked ? ' ic-chip--linked' : ''}${over ? ' ic-chip--over' : ''}`}
      onMouseEnter={over ? openPopup : undefined}
      onMouseLeave={over ? scheduleClose : undefined}
      title={over ? `Pediste ${deficit} de más — pasá el mouse para ver eventos` : (linked ? 'Vinculado con otro evento' : undefined)}
    >
      {over && (
        <span className="ic-chip-over" title={`Pediste ${deficit} de más`}>
          <WarnGlyph size={14} />
        </span>
      )}
      {group.length > 1 && <span className="ic-chip-count">{group.length} × </span>}
      <span className="ic-chip-label" title={label}>{linked && <LinkGlyph />}{isPack && <PackIcon />}{label}</span>
      {removable && (
        <button
          className="ic-chip-x"
          onMouseDown={e => { e.preventDefault(); e.stopPropagation(); onRemove?.(); }}
          title="Quitar"
        >✕</button>
      )}
      {over && open && rect && (
        <Popup
          conflict={{
            item_name: label,
            total: conflict.total,
            disponible: conflict.disponible,
            deficit,
            conflicts: conflict.conflicts || [],
          }}
          anchorRect={rect}
          onClose={() => setOpen(false)}
          onEventClick={(c) => { onEventClick?.(c); setOpen(false); }}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        />
      )}
    </span>
  );
}
